import { ConfigProvider } from "antd";
import { useSyncExternalStore, type ReactNode } from "react";

import { buildAntThemeConfig, DEFAULT_LOOK, STATIC_CSS_VAR_KEY, type AntLook } from "./antTheme";
import { readDocumentTheme, subscribeDocumentTheme, type ResolvedTheme } from "./theme";

/**
 * Theme for antd's STATIC APIs (`message.*`, `notification.*`, `Modal.confirm`): they render in a React
 * root of antd's own, outside the app's `ConfigProvider`, and would stay light on a dark page.
 * `ConfigProvider.config({ holderRender })` wraps every such holder; the wrapper follows `html[data-theme]`
 * live because antd reuses a holder for the life of the page. The accent is mirrored through
 * `setStaticPrimary` (the school's colour arrives after the first render).
 */
let staticPrimary: string | null = null;
let staticLook: AntLook = DEFAULT_LOOK;
const primaryListeners = new Set<() => void>();

export function setStaticPrimary(primary: string | null | undefined, look: AntLook = DEFAULT_LOOK) {
  staticPrimary = primary ?? null;
  staticLook = look;
  primaryListeners.forEach((l) => l());
}

const subscribePrimary = (l: () => void) => {
  primaryListeners.add(l);
  return () => primaryListeners.delete(l);
};

const getServerTheme = (): ResolvedTheme => "light";

const StaticApiTheme = ({ children }: { children?: ReactNode }) => {
  const resolved = useSyncExternalStore(subscribeDocumentTheme, readDocumentTheme, getServerTheme);
  const primary = useSyncExternalStore(subscribePrimary, () => staticPrimary, () => null);
  const look = useSyncExternalStore(subscribePrimary, () => staticLook, () => DEFAULT_LOOK);
  return <ConfigProvider theme={buildAntThemeConfig(resolved, primary, STATIC_CSS_VAR_KEY, look)}>{children}</ConfigProvider>;
};

/** Call once before React mounts: antd captures `holderRender` when it creates a holder. */
export function registerStaticAntTheme() {
  ConfigProvider.config({ holderRender: (children) => <StaticApiTheme>{children}</StaticApiTheme> });
}
